import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const BuyersDataGrid = ({ searchQuery, onEdit, onDelete, selectedItems, onSelectionChange }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });

  const buyersData = [
    {
      id: 'BUY001',
      name: 'Adani Power Limited',
      code: 'APL',
      country: 'India',
      contactPerson: 'Rajesh Kumar',
      email: 'r.kumar@adanipower.com',
      phone: '+91 79 2656 5555',
      status: 'active',
      creditLimit: 50000000,
      paymentTerms: 'LC 90 days',
      totalPurchases: 2450000,
      lastPurchase: '2025-12-08',
      syncStatus: 'synced'
    },
    {
      id: 'BUY002',
      name: 'Tata Power Company Limited',
      code: 'TPC',
      country: 'India',
      contactPerson: 'Priya Sharma',
      email: 'p.sharma@tatapower.com',
      phone: '+91 22 6665 8282',
      status: 'active',
      creditLimit: 45000000,
      paymentTerms: 'LC 75 days',
      totalPurchases: 3200000,
      lastPurchase: '2025-12-10',
      syncStatus: 'synced'
    },
    {
      id: 'BUY003',
      name: 'NTPC Limited',
      code: 'NTPC',
      country: 'India',
      contactPerson: 'Amit Patel',
      email: 'a.patel@ntpc.co.in',
      phone: '+91 11 2436 0100',
      status: 'active',
      creditLimit: 60000000,
      paymentTerms: 'LC 90 days',
      totalPurchases: 4100000,
      lastPurchase: '2025-12-11',
      syncStatus: 'pending'
    },
    {
      id: 'BUY004',
      name: 'JSW Energy Limited',
      code: 'JSW',
      country: 'India',
      contactPerson: 'Sunita Reddy',
      email: 's.reddy@jsw.in',
      phone: '+91 80 4747 4747',
      status: 'active',
      creditLimit: 35000000,
      paymentTerms: 'LC 60 days',
      totalPurchases: 1850000,
      lastPurchase: '2025-12-09',
      syncStatus: 'synced'
    },
    {
      id: 'BUY005',
      name: 'Reliance Power Limited',
      code: 'RPL',
      country: 'India',
      contactPerson: 'Vikram Singh',
      email: 'v.singh@reliancepower.com',
      phone: '+91 22 3303 1000',
      status: 'inactive',
      creditLimit: 40000000,
      paymentTerms: 'LC 90 days',
      totalPurchases: 2100000,
      lastPurchase: '2025-11-15',
      syncStatus: 'error'
    }
  ];

  const filteredData = buyersData?.filter(buyer =>
    buyer?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    buyer?.code?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    buyer?.contactPerson?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedData = [...filteredData]?.sort((a, b) => {
    if (sortConfig?.direction === 'asc') {
      return a?.[sortConfig?.key] > b?.[sortConfig?.key] ? 1 : -1;
    }
    return a?.[sortConfig?.key] < b?.[sortConfig?.key] ? 1 : -1;
  });

  const handleSelectAll = (checked) => {
    if (checked) {
      onSelectionChange(sortedData?.map(item => item?.id));
    } else {
      onSelectionChange([]);
    }
  };

  const handleSelectItem = (id, checked) => {
    if (checked) {
      onSelectionChange([...selectedItems, id]);
    } else {
      onSelectionChange(selectedItems?.filter(itemId => itemId !== id));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const getSyncStatusColor = (status) => {
    switch (status) {
      case 'synced': return 'text-success';
      case 'pending': return 'text-warning';
      case 'error': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-muted/50 border-b border-border">
          <tr>
            <th className="px-4 py-3 text-left">
              <Checkbox
                checked={selectedItems?.length === sortedData?.length && sortedData?.length > 0}
                onChange={(e) => handleSelectAll(e?.target?.checked)}
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('code')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Code
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('name')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Buyer Name
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact Person</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact Details</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              <button
                onClick={() => handleSort('creditLimit')}
                className="flex items-center gap-1 hover:text-foreground"
              >
                Credit Limit
                <Icon name="ArrowUpDown" size={14} />
              </button>
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Purchase History</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider">Sync</th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {sortedData?.map((buyer) => (
            <tr key={buyer?.id} className="hover:bg-muted/30 transition-colors">
              <td className="px-4 py-3">
                <Checkbox
                  checked={selectedItems?.includes(buyer?.id)}
                  onChange={(e) => handleSelectItem(buyer?.id, e?.target?.checked)}
                />
              </td>
              <td className="px-4 py-3">
                <span className="font-mono text-sm font-medium">{buyer?.code}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="font-medium text-foreground">{buyer?.name}</span>
                  <span className="text-xs text-muted-foreground">{buyer?.country}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="text-sm">{buyer?.contactPerson}</span>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Mail" size={12} />
                    <span>{buyer?.email}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="Phone" size={12} />
                    <span>{buyer?.phone}</span>
                  </div>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{formatCurrency(buyer?.creditLimit)}</span>
                  <span className="text-xs text-muted-foreground">{buyer?.paymentTerms}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{(buyer?.totalPurchases / 1000)?.toFixed(0)}K MT</span>
                  <span className="text-xs text-muted-foreground">Last: {buyer?.lastPurchase}</span>
                </div>
              </td>
              <td className="px-4 py-3">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  buyer?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                }`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    buyer?.status === 'active' ? 'bg-success' : 'bg-muted-foreground'
                  }`} />
                  {buyer?.status}
                </span>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-1">
                  <Icon
                    name={buyer?.syncStatus === 'synced' ? 'CheckCircle2' : buyer?.syncStatus === 'pending' ? 'Clock' : 'AlertCircle'}
                    size={16}
                    className={getSyncStatusColor(buyer?.syncStatus)}
                  />
                  <span className={`text-xs ${getSyncStatusColor(buyer?.syncStatus)}`}>
                    {buyer?.syncStatus}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Edit"
                    onClick={() => onEdit(buyer)}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Trash2"
                    onClick={() => onDelete(buyer?.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedData?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="Search" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No buyers found matching your search</p>
        </div>
      )}
    </div>
  );
};

export default BuyersDataGrid;